import { color } from "@ui";
import type { AnalysisFlag, AnalysisFlagMetadata } from "../PointMetadata";
import { ANALYSIS_FLAG_METADATA } from "../PointMetadata";
import t from "../../../../i18n";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { isActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Analysis } from "../../../../api/Analysis";
import { getAnalysesIncludedInReporting } from "./getAnalysisIncludedInReporting";
import { analysisStep } from "diagnostics-domain/analysisStep";
import type { Project } from "../../../../api/useProjects";
import { missingInputQuantities } from "../../../../api/getMissingInputQuantities";
import type { QuantityIndex } from "../../../../api/useQuantities";
import type { Setup } from "../../../../api/useSetups";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
export const ANALYSIS_FLAGS_SERIE_ID = "ANALYSIS_FLAGS_SERIE_ID";
export const FLAG_AXIS = {
  id: "flagAxis",
  visible: false,
  zoomEnabled: false,
  max: 1,
};

/**
 * @description
 * Flag priorities (the lower the priority, the higher the flag is displayed)
 *
 *  Ordre	Type	          Details                                         Flag
 *  ----------------------------------------------------------------------------------------------------
 *  1	    Info	          SYNCHRONIZING	                                  outdated
 *  2	    Erreur	        SIMULATION_ERROR	                              simulationError
 *  3	    Erreur	        DIAGNOSIS_ERROR	                                diagnosisError
 *  4	    Warning	        SIMULATION_ERROR_MISSING_BOUNDARY_CONDITION	    unavailableSimulation
 *  5	    Publication	    INCLUDED_IN_REPORTING	                          publishedAndIncludedAnalysis
 *  6	    Publication	    NOT_INCLUDED_IN_REPORTING	                      publishedNotIncludedAnalysis
 *  7	    Info	          DATA (= no valid simu setup)	                  uncompletedSimulation
 *  8	    Info	          SIMULATION (= no valid diag setup)	            uncompletedDiagnosis
 */
interface Flag {
  id: AnalysisFlag;
  description: (analysis: ActiveAnalysis, quantityIndex: QuantityIndex) => string;
  color: string;
  lineColor?: string;
  priority: number;
}

export const flagColors = {
  info: color.carbon400,
  warning: color.warning,
  error: color.error,
  publishedIncluded: color.chlorine300,
  publishedNotIncluded: color.chlorine200,
};

export const flagLineColors = {
  info: color.carbon400,
  warning: color.warning,
  error: color.error,
  publishedIncluded: color.chlorine900,
  publishedNotIncluded: color.titanium300,
};

const outdatedAnalysisFlag: Flag = {
  id: "outdated",
  color: flagColors.info,
  description: () => t("flags_legend.synchronizing"),
  priority: 1,
};

const publishedIncludedFlag: Flag = {
  id: "publishedAndIncludedAnalysis",
  description: () =>
    t("analysis.chart.tooltip.description.analysisPublication_status.included_in_reporting"),
  color: flagColors.publishedIncluded,
  lineColor: flagLineColors.publishedIncluded,
  priority: 5,
};

const publishedNotIncludedFlag: Flag = {
  id: "publishedNotIncludedAnalysis",
  description: () =>
    t("analysis.chart.tooltip.description.analysisPublication_status.not_included_in_reporting"),
  color: flagColors.publishedNotIncluded,
  lineColor: flagLineColors.publishedNotIncluded,
  priority: 6,
};

const analysisFlagPerStatus = {
  DATA: {
    id: "uncompletedSimulation" as const,
    description: () => t("symptoms.chart.tooltip.description.uncompletedSimulation"),
    color: flagColors.info,
    priority: 7,
  },
  SIMULATION_ERROR_MISSING_BOUNDARY_CONDITION: {
    id: "unavailableSimulation" as const,
    description: (analysis: ActiveAnalysis, quantityIndex: QuantityIndex) =>
      t(
        "symptoms.chart.tooltip.description.missingBoundaryConditions",
        missingInputQuantities(analysis, quantityIndex),
      ),
    color: flagColors.warning,
    priority: 4,
  },
  SIMULATION_ERROR: {
    id: "simulationError" as const,
    description: (analysis: ActiveAnalysis) => {
      const logs =
        analysis.simulationLogs?.errorType === "PYTHON"
          ? analysis.simulationLogs.errorCodes
          : analysis.simulationLogs;
      const statusInfo = logs ? JSON.stringify(logs).substring(0, 100) : "";
      return t("symptoms.chart.tooltip.description.simulationError", statusInfo);
    },
    color: flagColors.error,
    priority: 2,
  },
  SIMULATION: {
    id: "uncompletedDiagnosis" as const,
    description: () => t("diagnosis.chart.tooltip.description.uncompletedDiagnostic"),
    color: flagColors.info,
    priority: 8,
  },
  DIAGNOSIS_ERROR: {
    id: "diagnosisError" as const,
    description: (analysis: ActiveAnalysis) => {
      const logs =
        analysis.diagnosisLogs?.errorType === "PYTHON"
          ? analysis.diagnosisLogs.errorCodes
          : analysis.diagnosisLogs;

      const statusInfo = logs ? JSON.stringify(logs).substring(0, 100) : "";
      return t("diagnosis.chart.tooltip.description.diagnosticError", statusInfo);
    },
    color: flagColors.error,
    priority: 3,
  },
  DIAGNOSIS: null,
};

type ChartType = "MEASURES" | "SYMPTOMS" | "SUMMARY";

function getActiveAnalysisPointOptions(
  project: Project,
  setups: Setup[],
  analysis: ActiveAnalysis,
  analysesIncludedInReporting: Record<string, boolean>,
  quantityIndex: QuantityIndex,
  displayPublicationFlag = false,
) {
  const analysisStatus = analysis.status as keyof typeof analysisFlagPerStatus;
  let analysisFlag = analysisFlagPerStatus[analysisStatus];
  const setup = getAnalysisSetup(setups, analysis);
  // do not display missing simulation setup flag if the analysis has no setup at all
  if (
    analysisFlag &&
    setup === null &&
    (analysisFlag.id === "uncompletedSimulation" || analysisFlag.id === "uncompletedDiagnosis")
  ) {
    analysisFlag = null;
  }

  const outdatedFlag = analysisStep(project, analysis) === null ? null : outdatedAnalysisFlag;
  const publicationFlag = displayPublicationFlag
    ? analysis.isPublished
      ? analysesIncludedInReporting[analysis.id]
        ? publishedIncludedFlag
        : publishedNotIncludedFlag
      : null
    : null;

  const flag = [outdatedFlag, analysisFlag, publicationFlag]
    .filter(f => f !== null)
    .sort((a, b) => (a.priority || 0) - (b.priority || 0))[0];

  if (flag === undefined) return null;

  const custom: AnalysisFlagMetadata = {
    type: ANALYSIS_FLAG_METADATA,
    group: analysis.group,
    label: analysis.label,
    flagType: flag.id,
    human: null,
  };

  const events =
    flag.id === "simulationError"
      ? {
          click() {
            void window.navigator.clipboard.writeText(
              JSON.stringify(analysis.simulationLogs, null, 2),
            );
          },
        }
      : flag.id === "diagnosisError"
        ? {
            click() {
              void window.navigator.clipboard.writeText(
                JSON.stringify(analysis.diagnosisLogs, null, 2),
              );
            },
          }
        : undefined;

  return {
    id: `analysis-id-${analysis.id}`,
    x: analysis.date,
    y: 0,
    description: flag.description(analysis, quantityIndex),
    custom,
    color: flag.color,
    marker: {
      enabled: true,
      fillColor: flag.color,
      lineColor: "lineColor" in flag ? flag.lineColor : flag.color,
    },
    events,
  };
}

const syncMarker = {
  enabled: true,
  fillColor: flagColors.info,
  lineColor: flagColors.info,
};

function getAnalysisPointOptions(project: Project, analysis: Analysis) {
  if (!isActiveAnalysis(analysis)) return null;
  if (analysisStep(project, analysis) === null) return null;

  const custom: AnalysisFlagMetadata = {
    type: ANALYSIS_FLAG_METADATA,
    group: analysis.group,
    label: analysis.label,
    flagType: "outdated",
    human: null,
  };

  return {
    id: `analysis-id-${analysis.id}`,
    x: analysis.date,
    y: 0,
    description: t("flags_legend.synchronizing"),
    custom,
    color: flagColors.info,
    marker: syncMarker,
    events: undefined,
  };
}

const flagMarker = {
  symbol: "rectangle",
  lineWidth: 1,
  radius: 8,
};

export default function getAnalysisFlagSerie<
  CT extends ChartType,
  A extends CT extends "MEASURES" ? Analysis : ActiveAnalysis,
>({
  project,
  setups,
  analyses,
  quantityIndex,
  chartType,
  publicationFlagDisplayed,
}: {
  project: Project;
  setups: Setup[];
  analyses: A[];
  quantityIndex: QuantityIndex;
  chartType: CT;
  publicationFlagDisplayed: boolean;
}): Highcharts.SeriesScatterOptions {
  const { analysesIncludedInReporting } = getAnalysesIncludedInReporting(analyses);

  const data = analyses.reduce((acc: Highcharts.PointOptionsObject[], analysis: A) => {
    const point =
      chartType === "MEASURES"
        ? getAnalysisPointOptions(project, analysis)
        : getActiveAnalysisPointOptions(
            project,
            setups,
            analysis as ActiveAnalysis, // TS cannot infer that A is ActiveAnalysis when chartType is SYMPTOMS
            analysesIncludedInReporting,
            quantityIndex,
            publicationFlagDisplayed,
          );

    if (point !== null) acc.push(point);
    return acc;
  }, []);

  return {
    id: ANALYSIS_FLAGS_SERIE_ID,
    name: "",
    data,
    yAxis: FLAG_AXIS.id,
    type: "scatter",
    replicateInNavigator: false,
    color: color.lightNitrogen900, // hide hallow effect on point hover
    zIndex: zIndexChart.flagSeries,
    marker: flagMarker,
  };
}
